import { useParams } from 'react-router-dom';
import { Link } from 'wasp/client/router';
import { routes } from 'wasp/client/router';
import { useQuery } from 'wasp/client/operations';
import { getTopicBySlug } from 'wasp/client/operations';

export default function TopicDetailPage() {
  const { slug } = useParams();

  const {
    data: topic,
    isLoading,
    error,
  } = useQuery(getTopicBySlug, { slug: slug as string }) as any;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading topic...</p>
        </div>
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">Topic not found</p>
          <Link
            to={routes.TopicsRoute.to}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            ← Back to Topics
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to={routes.TopicsRoute.to}
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Topics
        </Link>

        {/* Main Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          {/* Header Image */}
          {topic.imageUrl && (
            <div className="h-64 overflow-hidden">
              <img
                src={topic.imageUrl}
                alt={topic.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8">
            {/* Topic Type Badge */}
            <div className="flex items-center gap-2 mb-4">
              {topic.topicType.icon && (
                <span className="text-2xl">{topic.topicType.icon}</span>
              )}
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                {topic.topicType.label}
              </span>
            </div>

            {/* Topic Name */}
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {topic.name}
            </h1>

            {/* Parent Topic */}
            {topic.parentTopic && (
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Under:{' '}
                <Link
                  to={routes.TopicDetailRoute.build({ params: { slug: topic.parentTopic.slug } }) as any}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {topic.parentTopic.name}
                </Link>
              </p>
            )}

            {/* Description */}
            {topic.description && (
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                {topic.description}
              </p>
            )}

            {/* Content */}
            {topic.content && (
              <div className="prose dark:prose-invert max-w-none mb-8">
                <div
                  dangerouslySetInnerHTML={{ __html: topic.content }}
                  className="text-gray-700 dark:text-gray-300"
                />
              </div>
            )}

            {/* Sub-Topics */}
            {topic.subTopics && topic.subTopics.length > 0 && (
              <div className="mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Sub-Topics
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {topic.subTopics.map((subTopic: any) => (
                    <Link
                      key={subTopic.id}
                      to={routes.TopicDetailRoute.build({ params: { slug: subTopic.slug } }) as any}
                      className="block p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        {subTopic.topicType?.icon && (
                          <span className="text-xl mt-0.5">{subTopic.topicType.icon}</span>
                        )}
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {subTopic.name}
                          </h3>
                          {subTopic.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                              {subTopic.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Services */}
            {topic.topicServices && topic.topicServices.length > 0 && (
              <div className="mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Related Services
                </h2>
                <div className="space-y-3">
                  {topic.topicServices.map((ts: any) => (
                    <div
                      key={ts.id}
                      className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {ts.content.title}
                      </h3>
                      {ts.content.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {ts.content.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        Location: {ts.content.location.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Occasions */}
            {topic.topicOccasions && topic.topicOccasions.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Related Occasions
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {topic.topicOccasions.map((to: any) => (
                    <Link
                      key={to.id}
                      to={routes.OccasionDetailRoute.build({ params: { slug: to.occasion.slug } }) as any}
                      className="block p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border border-blue-200 dark:border-blue-800"
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-xl">🌙</span>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {to.occasion.name}
                          </h3>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            {to.occasion.occasionType.label}
                          </p>
                          {to.occasion.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                              {to.occasion.description}
                            </p>
                          )}
                          {/* Show upcoming events if available */}
                          {to.occasion.events && to.occasion.events.length > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                              {to.occasion.events.length} upcoming event{to.occasion.events.length !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {(!topic.subTopics || topic.subTopics.length === 0) &&
              (!topic.topicServices || topic.topicServices.length === 0) &&
              (!topic.topicOccasions || topic.topicOccasions.length === 0) && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No related content available for this topic yet.</p>
                </div>
              )}
          </div>
        </div>
      </div>
  );
}



